/**
 * Licence Portal — public magic-link redemption page (LIGHT MODE).
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LicenceCollect() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: 'redeeming', error: null, licence: null });

  useEffect(() => {
    // Make the whole page background light for the public licence route.
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#F8FAFC';
    return () => { document.body.style.backgroundColor = prevBg; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${API}/api/licences/redeem/${token}`);
        sessionStorage.setItem('panorix_licence_session_token', token);
        sessionStorage.setItem('panorix_licence_session_meta', JSON.stringify(res.data));
        setState({ status: 'ok', licence: res.data, error: null });
        setTimeout(() => nav('/licences/view', { replace: true }), 900);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
        setState({ status: status === 410 ? 'expired' : status === 404 ? 'invalid' : 'error',
                   error: detail, licence: null });
      }
    })();
  }, [token, nav]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-6 py-16 bg-[#F8FAFC]">
      <div className="w-full max-w-md" data-testid="licence-collect">
        <div className="mb-8 text-center">
          <div className="text-[11px] tracking-[0.28em] text-slate-500 uppercase">Panorix Enterprise</div>
          <div className="text-3xl font-light text-slate-900 mt-2">Licence Portal</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-8">
          {state.status === 'redeeming' && (
            <div className="text-center py-6">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
              <div className="mt-4 text-slate-600">Verifying your link…</div>
            </div>
          )}

          {state.status === 'ok' && (
            <div className="text-center py-6" data-testid="licence-collect-success">
              <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
              <div className="mt-4 text-slate-900 text-lg font-medium">Link verified</div>
              <div className="text-slate-500 text-sm mt-1">Opening your licence…</div>
              <div className="mt-6 text-[11px] tracking-wider uppercase text-slate-400">
                {state.licence.customer_name} · {state.licence.tier?.toUpperCase()} · {state.licence.seats} seats
              </div>
            </div>
          )}

          {(state.status === 'expired' || state.status === 'invalid' || state.status === 'error') && (
            <div className="text-center py-6" data-testid="licence-collect-error">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
              <div className="mt-4 text-slate-900 text-lg font-medium">
                {state.status === 'expired' ? 'This link has expired'
                  : state.status === 'invalid' ? 'Link is invalid'
                  : 'Something went wrong'}
              </div>
              <div className="text-slate-600 text-sm mt-2 leading-relaxed">
                {state.error}
              </div>
              <div className="mt-6 text-[12px] text-slate-500">
                Contact your Panorix account manager at{' '}
                <a href="mailto:success@panorix.capexara.com" className="text-[#1E4E8C] underline">
                  success@panorix.capexara.com
                </a>{' '}
                to have a new link issued.
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 text-center text-[11px] text-slate-500">
          © Panorix Enterprise · <a href="/" className="hover:text-slate-700">panorix.capexara.com</a>
        </div>
      </div>
    </div>
  );
}
